/**
 * rd-expiry W2.1b — Frame 6 "Already booking" overlay.
 *
 * Shown when a creation attempt returns `409 ACTIVE_FLOW_EXISTS`. CSS copied from
 * the confirmed mockup (docs/mockups/client-dashboard-redesign/session-recovery-states.html,
 * Frame 6); the only deltas are the documented token swaps (radii → literal px,
 * --font-display → var(--client-font-heading), the theme's heading font; radii stay literal),
 * matching SessionDetailPage.module.css. Copy is verbatim from the frame and says
 * "reservation," never "hold."
 *
 *  • "Finish that booking"            (.rc-btn)        → Continue (EntryGate)
 *  • "Cancel it & book this instead"  (.rc-btn.ghost)  → Start over (cancel + retry)
 *
 * The existing-booking card shows the name (stacked over date/time) resolved from
 * the existing flow's context — name-only when no slot is selected yet.
 */

import { createPortal } from 'react-dom';
import { FocalImage } from '@/components/FocalImage';
import { formatWhen, getClientTimezone } from '@/pages/SessionDetailPage/detailFormat';
import type { ActiveFlowConflictCardData } from '@/hooks/flowConflict/useActiveFlowConflict';
import styles from './ActiveFlowConflictOverlay.module.css';

export interface ActiveFlowConflictOverlayProps {
  card: ActiveFlowConflictCardData;
  /** True while Start-over (cancel + retry) runs — disables both buttons. */
  resolving: boolean;
  onContinue: () => void;
  onStartOver: () => void;
}

export function ActiveFlowConflictOverlay({
  card,
  resolving,
  onContinue,
  onStartOver,
}: ActiveFlowConflictOverlayProps) {
  const tz = getClientTimezone();
  const whenText = card.whenIso ? formatWhen(card.whenIso, tz) : null;

  // On the body, so a page that makes its own layer (a custom site's checkout screen) can't trap it under the site's header or footer.
  return createPortal(
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="afc-title"
      aria-describedby="afc-sub"
      data-testid="active-flow-conflict"
    >
      <div className={`${styles.ovIcon} ${styles.warm}`} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      </div>

      <h2 id="afc-title" className={styles.ovTitle}>Finish your other booking first?</h2>
      <p id="afc-sub" className={styles.ovSub}>
        You&apos;ve already got a time reserved and haven&apos;t checked out. You can only have one
        active reservation at a time.
      </p>

      <div className={styles.ovCard}>
        <span className={styles.thumb} aria-hidden="true" data-testid="afc-thumb">
          {card.imageUrl && (
            <FocalImage
              src={card.imageUrl}
              alt=""
              focalPointX={card.focalPointX}
              focalPointY={card.focalPointY}
              focalPointScale={card.focalPointScale}
              className={styles.thumbImg}
            />
          )}
        </span>
        <span className={styles.meta}>
          <span className={styles.nm}>{card.name ?? 'Your reservation'}</span>
          {whenText && <span className={styles.wn}>{whenText}</span>}
        </span>
      </div>

      <div className={styles.rcActions}>
        <button
          type="button"
          className={styles.rcBtn}
          onClick={onContinue}
          disabled={resolving}
        >
          Finish that booking
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
        <button
          type="button"
          className={`${styles.rcBtn} ${styles.ghost}`}
          onClick={onStartOver}
          disabled={resolving}
        >
          {resolving ? 'Cancelling…' : 'Cancel it & book this instead'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
